import { useDebounce, useStableCallback } from "@prismicio/editor-support/React"
import {
  Box,
  FormSearchInput,
  HeaderTabLink,
  Icon,
  Line,
  Text
} from "@prismicio/editor-ui"
import { useState } from "react"
import { Link } from "react-router-dom"

import { releasesTabFlag } from "../app/experiments"
import { getRepositoryName } from "../app/routing"
import {
  headerHeight,
  mainContentMaxWidth,
  sidePanelWidth
} from "../document/documentLayout"
import { ReleasesDropdownMenu } from "./releases/ReleasesDropdownMenu"
import {
  type SearchDocumentParams,
  useSearchDocumentParams
} from "./useDocumentList"

interface DocumentListHeaderProps {
  tab: "working" | "archived" | "upcoming"
}

export function DocumentListHeader(props: DocumentListHeaderProps) {
  const { tab } = props

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={0}
      height={headerHeight}
      border={{ bottom: true }}
      backgroundColor="grey2"
      minWidth="100vw"
    >
      <Line color="purple" />
      <Box justifyContent="center" width="100vw" flexGrow={1}>
        <Box
          width={mainContentMaxWidth}
          justifyContent="center"
          alignItems="center"
          gap={24}
        >
          <Box padding={{ bottom: 12, top: 12 }}>
            <RepositoryDetails />
          </Box>
          <HeaderNavigationBar tab={tab} />
          <Box padding={{ bottom: 12, top: 12 }} flexGrow={1}>
            <SearchBar />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function RepositoryDetails() {
  return (
    <Box gap={16} alignItems="center" minWidth={sidePanelWidth}>
      <Box backgroundColor="grey5" borderRadius={6} padding={8}>
        <Icon name="prismic" color="grey12" size="large" />
      </Box>
      <Text variant="bold">{getRepositoryName()}</Text>
    </Box>
  )
}

interface HeaderNavigationBarProps {
  tab: "working" | "archived" | "upcoming"
}

function HeaderNavigationBar(props: HeaderNavigationBarProps) {
  const { tab } = props

  const { urlSearchParams } = useSearchDocumentParams()
  const releasesFlag = releasesTabFlag() === "on"

  return (
    <Box gap={16} height="100%" position="relative" left={-8}>
      <HeaderTabLink
        text="Work"
        renderLink={(children) => (
          <Link to={`/working${urlSearchParams}`}>{children}</Link>
        )}
        selected={tab === "working"}
      />
      <HeaderTabLink
        text="Planned"
        renderLink={(children) =>
          releasesFlag ? (
            <Link to={`/upcoming${urlSearchParams}`}>
              <ReleasesDropdownMenu>{children}</ReleasesDropdownMenu>
            </Link>
          ) : (
            children
          )
        }
        disabled={!releasesFlag}
        selected={tab === "upcoming"}
      />
      <HeaderTabLink
        text="Archived"
        renderLink={(children) => (
          <Link to={`/archived${urlSearchParams}`}>{children}</Link>
        )}
        selected={tab === "archived"}
      />
    </Box>
  )
}

function SearchBar() {
  const {
    searchDocumentParams,
    updateSearchDocumentParams,
    isLoadingDocuments
  } = useSearchDocumentParams()

  const urlSearchTerm = searchDocumentParams.term?.toString() || ""

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm)

  useDebounce(
    searchTerm,
    300,
    useStableCallback(() =>
      updateSearchTerm({
        searchTerm,
        urlSearchTerm,
        updateSearchDocumentParams
      })
    )
  )

  return (
    <Box width="100%" flexDirection="column">
      <FormSearchInput
        value={searchTerm}
        placeholder="Search"
        onValueChange={setSearchTerm}
        maxLength={200}
        isLoading={isLoadingDocuments && searchTerm !== urlSearchTerm}
      />
    </Box>
  )
}

interface updateSearchTermParams {
  searchTerm: string
  urlSearchTerm: string
  updateSearchDocumentParams: (params: SearchDocumentParams) => void
}
function updateSearchTerm(params: updateSearchTermParams) {
  const { searchTerm, urlSearchTerm, updateSearchDocumentParams } = params

  // if search term is empty and the url is not, we reset the url
  if (urlSearchTerm !== "" && searchTerm === "")
    return updateSearchDocumentParams({ term: [""] })

  // Do not update when search term is empty
  if (searchTerm === "") return

  updateSearchDocumentParams({ term: [searchTerm] })
}
